import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FormQuestion, FormProvider } from "@/pages/forms/types/form";
import { PROVIDER_LABEL } from "@/features/forms/constants";

export default function FormQuestionsCard({
  questions,
  provider,
  action,
}: {
  questions: FormQuestion[];
  provider: FormProvider;
  action?: React.ReactNode;
}) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return questions;
    return questions.filter(
      (x) =>
        (x.title ?? "").toLowerCase().includes(s) ||
        (x.type ?? "").toLowerCase().includes(s) ||
        (x.id ?? "").toLowerCase().includes(s),
    );
  }, [questions, q]);

  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="pb-3 space-y-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Questions ({questions.length})
          </CardTitle>
          {action}
        </div>
        <Input
          placeholder="Search questions…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-8"
        />
      </CardHeader>
      <CardContent>
        {questions.length === 0 ? (
          <p className="text-sm text-gray-400">
            No questions synced — use Re-fetch to pull them from{" "}
            {PROVIDER_LABEL[provider]}.
          </p>
        ) : (
          <div className="h-[360px] overflow-y-auto rounded-md border border-gray-100">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-1.5 px-3 w-10">#</th>
                  <th className="py-1.5 px-3">Title</th>
                  <th className="py-1.5 px-3">Type</th>
                  <th className="py-1.5 px-3">id</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((x, i) => (
                  <tr key={x.id ?? i} className="border-t border-gray-100">
                    <td className="py-1.5 px-3 text-gray-400">{i + 1}</td>
                    <td className="py-1.5 px-3">{x.title ?? "—"}</td>
                    <td className="py-1.5 px-3 text-gray-500">
                      {x.type ?? "—"}
                    </td>
                    <td className="py-1.5 px-3 font-mono text-[11px] text-gray-400">
                      {x.id}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-4 px-3 text-center text-sm text-gray-400"
                    >
                      No matches
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
